/* Copyright (c) 2016-2025 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import fieldManager from "../util/fields.js";
import util from "../util/util.js";

const program = {
	code: "obp",
	name: "Orange-bellied Parrot",
	description: `
		<p style="font-weight: bold">🚧 This program is in development and is only visible to Administrator users.</p>
		<p>
			TODO: Program description
		</p>`,
	load() {
		return birdata.lookup().then(function(lookups) {
			program.options = lookups;

			let codes = new Set([
				'incidental_search',
				'area_search_within_500_m',
				'area_search_within_5_km',
				'fixed_route_search',
				'5_min_point_search'
			]);
			program.surveyTypeOptions = program.options.survey_type.filter(st => codes.has(st.code));
		});
	},
	canParticipate(user) {
		return user.is_administrator || user.permissions?.some(function(p) {
			return p.type == "program" && p.program == "obp";
		});
	},
	canFilterBy(user) {
		return this.canParticipate(user);
	},
	fields: function() {
		let fields = [
			{
				name: "windSpeed",
				type: "options",
				options: program.options.obp_wind_speed
			},
			{
				name: "rain",
				type: "options",
				options: program.options.obp_rain
			},
			{
				name: "cloudCover",
				type: "options",
				options: program.options.obp_cloud_cover
			},
			{
				name: "minTemperatureCelsius",
				type: "number",
				label: "Minimum temperature",
				units: "°C",
				max: 80,
				min: -30,
				decimalPlaces: 1,
				validate: ">=-30,<=80"
			},
			{
				name: "maxTemperatureCelsius",
				type: "number",
				label: "Maximum temperature",
				units: "°C",
				max: 80,
				min: -30,
				decimalPlaces: 1,
				validate: [">=-30,<=80", function(f) {
					if(parseFloat(fields.minTemperatureCelsius()) > parseFloat(fields.maxTemperatureCelsius())) {
						return "Maximum temperature must not be less than minimum temperature"
					}
				}]
			},
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, f => f.field.name);

		return fields;

	},
	sightingFields: function(sightingModel) {
		let isObp = sightingModel.species.common_name == 'Orange-bellied Parrot';

		// For convenience, this is used to show/hide all of the OBP fields on the sighting details tab
		sightingModel._isObp = isObp;

		let fields = [
			{
				name: "roostingHabitat",
				type: "text",
				options: [
					"Allocasuarina sp.",
					"Dune/ saltmarsh",
					"Man made structures",
					"Melaleuca sp.",
					"Phragmites australis",
					"Spiny Rush (Juncus acutus)",
					"Twiggy turnip (Brassica fruticulosa)",
					"Eucalypt"
				],
				hidden: () => !isObp
			},
			{
				name: "habitat",
				type: "text",
				options: [
					"Adjacent to pond",
					"Adjacent to pond/Juncus",
					"Channel",
					"Dune/Saltmarsh",
					"Embankment",
					"Juncus",
					"Melaleuca",
					"Other",
					"Phragmites",
					"Road",
					"Saltmarsh",
					"Saltmarsh/Structure",
					"Tea Tree",
					"Track",
					"Track/Phragmites",
					"Track/Rocky embankment",
					"Weeds",
					"Buttongrass plain",
					"Button grass with emergents"
				],
				hidden: () => !isObp
			},
			{
				name: "flightDirection",
				type: "options",
				options: program.options.approach_direction,
				hidden: () => !isObp
			},
			{
				name: "behaviour",
				label: "General behaviour",
				type: "options",
				options: program.options.obp_behaviour,
				hidden: () => !isObp
			},
			{
				name: "leftLegBandColour",
				type: "options",
				options: program.options.obp_band_colour,
				hidden: () => !isObp
			},
			{
				name: "leftLegBandLetter",
				type: "text",
				maxLength: 1,
				hidden: () => !isObp
			},
			{
				name: "rightLegBandColour",
				type: "options",
				options: program.options.obp_band_colour,
				hidden: () => !isObp
			},
			{
				name: "rightLegBandLetter",
				type: "text",
				maxLength: 1,
				hidden: () => !isObp
			},
			{
				name: "sex",
				type: "options",
				options: program.options.sex,
				hidden: () => !isObp
			},
			{
				name: "feedingPlant",
				type: "text",
				options: [
					"Annual Veldt-grass",
					"Austral Seablite",
					"Beaded Glasswort",
					"Buck's-horn Plantain",
					"Canary Grass",
					"Carpetweed",
					"Chenopod family_Atriplex and Chenopodium",
					"Clover",
					"Coastal Saltbush",
					"Common Reed",
					"Curled Dock",
					"Dock",
					"Fat hen",
					"Giant Mustard",
					"Glaucous Goosefoot",
					"Hastate Orache",
					"Hogweed",
					"Perennial Rye-grass",
					"Rye Grass",
					"Sand Rocket",
					"Sea barley",
					"Seaberry saltbush",
					"Sea-heath",
					"Sheoak",
					"Sheperds purse",
					"Shrubby Glasswort",
					"Spiny Rush",
					"Toowoomba Canary-grass",
					"Twiggy Turnip",
					"Unknown_grasseeds",
					"Wild Oat",
					"Wireweed"
				],
				hidden: () => !isObp
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, f => f.field.name);

		return fields;
	},
	programData: function(survey) {
		let fields = program.fields(survey);

		return { fields };
	},
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().obp.fields;
		json.obp = {};

		for(let f of Object.values(fields)) {
			f.field.save(json.obp);
		}
	},
	preprocessJSON: function(json) {
		if(!json.obp) return;

		if(json._preprocessed) return;
		json._preprocessed = true;

		// Ensures that all sightings have an obp property
		for(let sighting of (json.sightings || [])) {
			if(!sighting.obp) {
				sighting.obp = {};
			}
		}
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.obp;

		if(!data) return; //incomplete survey

		this.preprocessJSON(json);

		for(let f of Object.values(fields)) {
			f.field.load(data);
		}
	},
	// updateSightingJSON: function(json, sighting, survey) {
	// 	let data = json.obp;
	// },
	visibleSharedSiteLayers: () => ['obp'],
	defaultAreaLayer: (layers) => null,
	filterAreaLayers: (layer) => false,
	hideWaterLevel: true,
	hideSurveyType: false,
	surveyTypeOptions: null, // See load()
	afterSelect: function(survey) {
		survey.type(program.options.survey_type.find(st => st.code == 'incidental_search'))
	},
	beforeDeselect: function(survey) {
		survey.type(null)
	},
	canAssignSurveyOwner: true,
	allSpeciesRecordedEnabled: () => false,
	multipleDaysEnabled: () => true,
	observerCountEnabled: () => true,
	observerCountIsOptional: () => true,
	siteBasedEntry: true,
	recordSiteID: true,
	forcePrivate: true,
	sightingLocations: true,
	forceSightingLocations: false,
	multipleSightingsPerSpecies: true,
	validateDuration(duration, survey) {
		// Survey duration is always required (even for incidental_search)
		if(!duration) {
			return "Survey duration is required";
		}
	}
};

export default program;
